/**
 * Hero Carousel – component-based Swiper for hero-carousel section.
 * Uses window.Swiper (theme loads swiper7.4.1.min.js).
 */

const SELECTOR_CAROUSEL = '.js-heroCarousel';
const SELECTOR_NAV = '.js-heroCarouselNav';

const waitForSwiper = (timeoutMs = 5000) => new Promise((resolve, reject) => {
  if (window.Swiper) return resolve(window.Swiper);
  const start = Date.now();
  const id = setInterval(() => {
    if (window.Swiper) {
      clearInterval(id);
      resolve(window.Swiper);
    } else if (Date.now() - start > timeoutMs) {
      clearInterval(id);
      reject(new Error('Swiper failed to load within timeout.'));
    }
  }, 50);
});

class HeroCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this._sectionLoadHandler = this._onSectionChange.bind(this);
    this._sectionUnloadHandler = this._onSectionChange.bind(this);
  }

  connectedCallback() {
    this.init();
    document.addEventListener('shopify:section:load', this._sectionLoadHandler);
    document.addEventListener('shopify:section:unload', this._sectionUnloadHandler);
  }

  disconnectedCallback() {
    this.destroy();
    document.removeEventListener('shopify:section:load', this._sectionLoadHandler);
    document.removeEventListener('shopify:section:unload', this._sectionUnloadHandler);
  }

  _onSectionChange(evt) {
    const target = evt && evt.target;
    if (!target || typeof target.querySelector !== 'function') return;
    if (target.querySelector('[data-component="heroCarousel"]')) {
      this.destroy();
      this.init();
    }
  }

  _parseAutoplay(value) {
    if (!value) return false;
    try {
      return JSON.parse(value);
    } catch (e) {
      return false;
    }
  }

  async init() {
    const elCarousel = this.querySelector(SELECTOR_CAROUSEL);
    if (!elCarousel) return;
    if (elCarousel.classList.contains('swiper-initialized')) return;

    let Swiper;
    try {
      Swiper = await waitForSwiper();
    } catch (err) {
      console.warn('[hero-carousel]', err.message);
      return;
    }

    if (elCarousel.classList.contains('swiper-initialized')) return;

    const slides = elCarousel.querySelectorAll('.swiper-slide');
    const hasMultipleSlides = slides.length > 1;

    const nextEl = this.querySelector(`${SELECTOR_NAV}.next`);
    const prevEl = this.querySelector(`${SELECTOR_NAV}.prev`);
    const autoplay = this._parseAutoplay(elCarousel.dataset.autoplay);

    const options = {
      centeredSlides: true,
      loop: hasMultipleSlides,
      autoplay: hasMultipleSlides ? autoplay : false,
      navigation: nextEl && prevEl ? { nextEl, prevEl } : false,
      updateOnImagesReady: true,
    };

    this.swiper = new Swiper(elCarousel, options);
  }

  destroy() {
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
